import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, interval, map, takeWhile, Observable } from 'rxjs';

const STORE_KEY = 'userLastAction';

@Injectable({ providedIn: 'root' })
export class IdleService implements OnDestroy {

  public static runInitialTimer: boolean;
  public static runFinalTimer: boolean;
  public clockForIdle: Observable<number>;

  private INITIAL_IDLE_TIME_IN_MIN: number;
  private FINAL_IDLE_TIME_IN_MIN: number;

  private initialIdlenessStatusSubject: BehaviorSubject<string>;
  private finalIdlenessStatusSubject: BehaviorSubject<string>;
  public cameraFoundSubject: BehaviorSubject<boolean>;

  private sessionForIdle: Observable<number>;
  private userActivityChangeCallback: ($event) => void;

  constructor(private zone: NgZone) {
    this.initialIdlenessStatusSubject = new BehaviorSubject<string>('INITIATE_TIMER');
    this.finalIdlenessStatusSubject = new BehaviorSubject<string>('INITIATE_SECOND_TIMER');
    this.cameraFoundSubject=new BehaviorSubject<boolean>(false);
  }

  set initialIdleTime(minutes: number) {
    this.INITIAL_IDLE_TIME_IN_MIN = minutes;
  }

  set finalIdleTime(minutes: number) {
    this.FINAL_IDLE_TIME_IN_MIN = minutes;
  }

  get cameraFound$(): Observable<boolean> {
    return this.cameraFoundSubject.asObservable();
  }

  get initialIdlenesStatus$(): Observable<string> {
    return this.initialIdlenessStatusSubject.asObservable();
  }

  get finalIdlenessStatus$(): Observable<string> {
    return this.finalIdlenessStatusSubject.asObservable();
  }

  get lastAction(): number {
    return parseInt(localStorage.getItem(STORE_KEY), 10);
  }

  set lastAction(value) {
    localStorage.setItem(STORE_KEY, value.toString());
  }

  public initilizeSessionTimeout(): void {
    IdleService.runInitialTimer = true;
    this.reset();
    this.initListener();
    this.initInterval();
  }

  public stopSessionTimeout(): void {
    if (this.initialIdlenessStatusSubject && this.INITIAL_IDLE_TIME_IN_MIN > 0) {
      IdleService.runInitialTimer = false;
      window.document.removeEventListener('click', this.userActivityChangeCallback, true);
      this.initialIdlenessStatusSubject.next('KILL_TIMER');
    }
  }

  public reset(): void {
    this.lastAction = Date.now();
    if (this.initialIdlenessStatusSubject) {
      this.initialIdlenessStatusSubject.next('RESET_TIMER');
    }
  }

  public removeActionFromStore(): void {
    localStorage.removeItem(STORE_KEY);
  }

  handleUserActiveState(event): void {
    if(IdleService.runInitialTimer ==true){
      this.reset();
    }
    else{
      if (this.initialIdlenessStatusSubject.value !="STOPPED_TIMER") {
        this.reset();
      }
    }
  }

  private initListener(): void {
    this.zone.runOutsideAngular(() => {
      this.userActivityChangeCallback = ($event) => this.handleUserActiveState($event);
      window.document.addEventListener('click', this.userActivityChangeCallback.bind(this), true);
    });
  }

  private initInterval(): void {
    const intervalDuration = 1000;
    this.sessionForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => IdleService.runInitialTimer)
    );

    this.check();
  }

  private check(): void {
    this.sessionForIdle
      .subscribe(() => {
        const now = Date.now();
        const timeleft = this.lastAction + this.INITIAL_IDLE_TIME_IN_MIN * 60 * 1000;
        const diff = timeleft - now;
        const isTimeout = diff < 0;

        this.initialIdlenessStatusSubject.next(`${diff}`);

        if (isTimeout) {
          window.document.removeEventListener('click', this.userActivityChangeCallback, true);
          this.zone.run(() => {
            if (this.initialIdlenessStatusSubject) {
              this.initialIdlenessStatusSubject.next('STOPPED_TIMER');

              if (this.FINAL_IDLE_TIME_IN_MIN > 0) {
                this.finalIdlenessStatusSubject.next('SECOND_TIMER_STARTED');
                this.executeFinalTimer();
              }
            }
            IdleService.runInitialTimer = false;
          });
        }
      });
  }

  private executeFinalTimer = () => {
    IdleService.runFinalTimer = true;
    this.initializeFinalTimer();
  }

  private initializeFinalTimer(): void {
    const intervalDuration = 5000;
    this.clockForIdle = interval(intervalDuration).pipe(
      map((tick: number) => {
        return tick;
      }),
      takeWhile(() => IdleService.runFinalTimer)
    );

    this.checkUserActionTime();
  }

  private checkUserActionTime(): void {
    this.clockForIdle.subscribe(() => {
      const now = Date.now();
      const timeleft = this.lastAction + (this.INITIAL_IDLE_TIME_IN_MIN + this.FINAL_IDLE_TIME_IN_MIN) * 60 * 1000;
      const diff = timeleft - now;
      const isTimeout = diff < 0;
      if (isTimeout) {
        IdleService.runFinalTimer = false;
        if (this.finalIdlenessStatusSubject) {
          this.finalIdlenessStatusSubject.next('SECOND_TIMER_STOPPED');
        }
      }
      this.finalIdlenessStatusSubject.next(`${diff}`);
    });
  }
  
  ngOnDestroy(): void {
    if (this.initialIdlenessStatusSubject) {
      this.initialIdlenessStatusSubject.unsubscribe();
      this.initialIdlenessStatusSubject = undefined;
    }

    if (this.finalIdlenessStatusSubject) {
      this.finalIdlenessStatusSubject.unsubscribe();
      this.finalIdlenessStatusSubject = undefined;
    }
  }

}
